/**
 * Product Image Carousel - Component JavaScript
 * A lightweight, accessible carousel component for product pages
 */

class ProductImageCarousel {
  /**
   * @param {HTMLElement} element - The carousel container element
   */
  constructor(element) {
    this.carousel = element;
    this.track = element.querySelector('.product-image-carousel__track');
    this.slides = element.querySelectorAll('.product-image-carousel__slide');
    this.prevButton = element.querySelector('.product-image-carousel__arrow--prev');
    this.nextButton = element.querySelector('.product-image-carousel__arrow--next');
    this.dots = element.querySelectorAll('.product-image-carousel__dot');

    this.currentIndex = 0;
    this.slidesCount = this.slides.length;
    this.autoplayInterval = null;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;

    this.#parseSettings();
    this.#init();

    console.info(`[ProductImageCarousel] Initialized carousel ${this.carousel.id} with ${this.slidesCount} slides`);
  }

  /**
   * Parse carousel settings from data attributes
   */
  #parseSettings() {
    this.settings = {
      autoplay: this.carousel.dataset.autoplay === 'true',
      autoplaySpeed: parseInt(this.carousel.dataset.autoplaySpeed, 10) || 5000,
      showArrows: this.carousel.dataset.showArrows === 'true',
      showDots: this.carousel.dataset.showDots === 'true',
      infinite: this.carousel.dataset.infinite === 'true',
    };

    console.debug('[ProductImageCarousel] Settings:', this.settings);
  }

  /**
   * Initialize carousel functionality
   */
  #init() {
    if (this.slidesCount <= 1) {
      console.debug('[ProductImageCarousel] Single slide detected, skipping navigation setup');
      return;
    }

    this.#bindEvents();
    this.#updateArrowStates();

    if (this.settings.autoplay) {
      this.#startAutoplay();
    }
  }

  /**
   * Bind all event listeners
   */
  #bindEvents() {
    // Arrow navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.#goToPrevSlide());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.#goToNextSlide());
    }

    // Dot navigation
    for (const dot of this.dots) {
      dot.addEventListener('click', (event) => {
        const slideIndex = parseInt(event.currentTarget.dataset.slide, 10);
        this.#goToSlide(slideIndex);
      });
    }

    // Touch/Mouse events for swipe
    this.track.addEventListener('mousedown', (e) => this.#handleDragStart(e));
    this.track.addEventListener('touchstart', (e) => this.#handleDragStart(e), { passive: true });
    this.track.addEventListener('mousemove', (e) => this.#handleDragMove(e));
    this.track.addEventListener('touchmove', (e) => this.#handleDragMove(e), { passive: true });
    this.track.addEventListener('mouseup', () => this.#handleDragEnd());
    this.track.addEventListener('mouseleave', () => this.#handleDragEnd());
    this.track.addEventListener('touchend', () => this.#handleDragEnd());

    // Pause autoplay on hover
    if (this.settings.autoplay) {
      this.carousel.addEventListener('mouseenter', () => this.#stopAutoplay());
      this.carousel.addEventListener('mouseleave', () => this.#startAutoplay());
    }

    // Keyboard navigation
    this.carousel.addEventListener('keydown', (e) => this.#handleKeydown(e));

    // Handle visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.#stopAutoplay();
      } else if (this.settings.autoplay) {
        this.#startAutoplay();
      }
    });
  }

  /**
   * Navigate to the previous slide
   */
  #goToPrevSlide() {
    if (this.settings.infinite) {
      this.currentIndex = this.currentIndex === 0 ? this.slidesCount - 1 : this.currentIndex - 1;
    } else {
      this.currentIndex = Math.max(0, this.currentIndex - 1);
    }

    this.#updateCarousel();
    console.debug(`[ProductImageCarousel] Navigated to slide ${this.currentIndex}`);
  }

  /**
   * Navigate to the next slide
   */
  #goToNextSlide() {
    if (this.settings.infinite) {
      this.currentIndex = this.currentIndex === this.slidesCount - 1 ? 0 : this.currentIndex + 1;
    } else {
      this.currentIndex = Math.min(this.slidesCount - 1, this.currentIndex + 1);
    }

    this.#updateCarousel();
    console.debug(`[ProductImageCarousel] Navigated to slide ${this.currentIndex}`);
  }

  /**
   * Navigate to a specific slide
   * @param {number} index - The slide index to navigate to
   */
  #goToSlide(index) {
    if (index >= 0 && index < this.slidesCount) {
      this.currentIndex = index;
      this.#updateCarousel();
      console.debug(`[ProductImageCarousel] Navigated to slide ${this.currentIndex}`);
    }
  }

  /**
   * Update carousel position and states
   */
  #updateCarousel() {
    const translateX = -this.currentIndex * 100;
    this.track.style.transform = `translateX(${translateX}%)`;
    this.prevTranslate = translateX;

    this.#updateDots();
    this.#updateArrowStates();
  }

  /**
   * Update dot navigation active state
   */
  #updateDots() {
    for (const dot of this.dots) {
      const dotIndex = parseInt(dot.dataset.slide, 10);
      dot.classList.toggle('product-image-carousel__dot--active', dotIndex === this.currentIndex);
    }
  }

  /**
   * Update arrow button disabled states
   */
  #updateArrowStates() {
    if (!this.settings.infinite) {
      if (this.prevButton) {
        this.prevButton.disabled = this.currentIndex === 0;
      }
      if (this.nextButton) {
        this.nextButton.disabled = this.currentIndex === this.slidesCount - 1;
      }
    }
  }

  /**
   * Start autoplay
   */
  #startAutoplay() {
    if (this.autoplayInterval) return;

    this.autoplayInterval = setInterval(() => {
      this.#goToNextSlide();
    }, this.settings.autoplaySpeed);

    console.debug('[ProductImageCarousel] Autoplay started');
  }

  /**
   * Stop autoplay
   */
  #stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
      console.debug('[ProductImageCarousel] Autoplay stopped');
    }
  }

  /**
   * Handle drag/swipe start
   * @param {MouseEvent|TouchEvent} event
   */
  #handleDragStart(event) {
    this.isDragging = true;
    this.carousel.classList.add('product-image-carousel--dragging');
    this.startX = this.#getPositionX(event);
    this.currentTranslate = this.prevTranslate;
  }

  /**
   * Handle drag/swipe move
   * @param {MouseEvent|TouchEvent} event
   */
  #handleDragMove(event) {
    if (!this.isDragging) return;

    const currentX = this.#getPositionX(event);
    const diff = currentX - this.startX;
    const slideWidth = this.carousel.offsetWidth;
    const translatePercent = (diff / slideWidth) * 100;

    this.currentTranslate = this.prevTranslate + translatePercent;
    this.track.style.transform = `translateX(${this.currentTranslate}%)`;
  }

  /**
   * Handle drag/swipe end
   */
  #handleDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.carousel.classList.remove('product-image-carousel--dragging');

    const movedPercent = this.currentTranslate - this.prevTranslate;
    const threshold = 20; // Minimum percentage to trigger slide change

    if (movedPercent < -threshold && this.currentIndex < this.slidesCount - 1) {
      this.#goToNextSlide();
    } else if (movedPercent > threshold && this.currentIndex > 0) {
      this.#goToPrevSlide();
    } else {
      // Snap back to current slide
      this.#updateCarousel();
    }
  }

  /**
   * Get X position from mouse or touch event
   * @param {MouseEvent|TouchEvent} event
   * @returns {number}
   */
  #getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.#goToPrevSlide();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#goToNextSlide();
        break;
    }
  }

  /**
   * Destroy carousel instance
   */
  destroy() {
    this.#stopAutoplay();
    console.info(`[ProductImageCarousel] Destroyed carousel ${this.carousel.id}`);
  }
}

/**
 * Initialize all carousels on the page
 */
const initializeCarousels = () => {
  const carousels = document.querySelectorAll('.product-image-carousel');
  
  for (const carousel of carousels) {
    if (!carousel.dataset.initialized) {
      new ProductImageCarousel(carousel);
      carousel.dataset.initialized = 'true';
    }
  }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCarousels);
} else {
  initializeCarousels();
}

// Re-initialize when Shopify section is re-rendered (Theme Editor)
document.addEventListener('shopify:section:load', initializeCarousels);
document.addEventListener('shopify:block:select', initializeCarousels);
